import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  X, Check, Eye, GripVertical, Mail, Calendar, Sparkles,
  ChevronDown, ChevronUp, Play, Building2, Package
} from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';

const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  general: 'bg-blue-100 text-blue-700',
  marketing: 'bg-emerald-100 text-emerald-700',
  development: 'bg-violet-100 text-violet-700',
  retention: 'bg-amber-100 text-amber-700',
};

export default function UpsellCampaignBuilder({ entry, productTypes, onClose, getAuthHeaders }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]); // [{key, day}]
  const [existingCampaign, setExistingCampaign] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);

  const headers = getAuthHeaders();
  const clientInfo = {
    contact_name: entry.contact_name,
    contact_email: entry.contact_email,
    business_name: entry.business_name,
    website: entry.website,
    package: entry.package,
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/onboarding/upsell/templates`, { headers }),
        axios.get(`${API}/admin/onboarding/upsell/campaign/${entry.product_type}/${entry.id}`, { headers }),
      ]);
      setTemplates(tRes.data.templates || []);

      const campaign = cRes.data.campaign;
      if (campaign) {
        setExistingCampaign(campaign);
        setSelected(campaign.sequence.map(s => ({ key: s.template_key, day: s.day })));
      }
    } catch (err) {
      console.error('Error fetching upsell data:', err);
    }
    setLoading(false);
  }, [entry.product_type, entry.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleTemplate = (key) => {
    setSelected(prev => {
      const exists = prev.find(s => s.key === key);
      if (exists) return prev.filter(s => s.key !== key);
      const tmpl = templates.find(t => t.key === key);
      return [...prev, { key, day: tmpl?.default_day || 7 }];
    });
  };

  const updateDay = (key, day) => {
    setSelected(prev => prev.map(s => s.key === key ? { ...s, day: parseInt(day) || 1 } : s));
  };

  const handlePreview = async (templateKey) => {
    if (previewKey === templateKey) {
      setPreviewKey(null);
      setPreviewHtml(null);
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/onboarding/upsell/preview`, {
        template_key: templateKey, client_info: clientInfo,
      }, { headers });
      setPreviewKey(templateKey);
      setPreviewHtml(r.data);
    } catch (err) {
      alert('Preview failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSave = async () => {
    if (selected.length === 0) return alert('Select at least one email template');
    setSaving(true);
    try {
      await axios.post(`${API}/admin/onboarding/upsell/build`, {
        product_type: entry.product_type,
        entry_id: entry.id,
        templates: selected,
        client_info: clientInfo,
      }, { headers });
      await fetchData();
      alert('Campaign saved as draft! Review the sequence, then click Activate when ready.');
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleActivate = async () => {
    if (!window.confirm('Activate this upsell campaign? Emails will start sending on schedule.')) return;
    setActivating(true);
    try {
      await axios.put(`${API}/admin/onboarding/upsell/activate/${entry.product_type}/${entry.id}`, {}, { headers });
      await fetchData();
      alert('Campaign activated! Emails will be sent on the scheduled days.');
    } catch (err) {
      alert('Activation failed: ' + (err.response?.data?.detail || err.message));
    }
    setActivating(false);
  };

  const sortedSelected = [...selected].sort((a, b) => a.day - b.day);
  const isActive = existingCampaign?.status === 'active';
  const isDraft = existingCampaign?.status === 'draft';
  const config = productTypes[entry.product_type] || {};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upsell-builder-modal">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
          <div>
            <h3 className="font-bold text-lg">Upsell Campaign Builder</h3>
            <span className="text-sm text-slate-500">{entry.business_name} — {config.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {isActive && <Badge className="bg-emerald-500">Active</Badge>}
            {isDraft && <Badge className="bg-amber-500">Draft — Review Required</Badge>}
            <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading templates...</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Client Info */}
            <div className="bg-slate-50 rounded-lg p-4 flex flex-wrap gap-6 text-sm" data-testid="client-info-panel">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /><span className="font-medium">{entry.business_name}</span></div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span>{entry.contact_email || '—'}</span></div>
              <div className="flex items-center gap-2"><Package className="h-4 w-4 text-slate-400" /><span>Purchased: {entry.package || config.label || '—'}</span></div>
            </div>

            {isActive && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800" data-testid="active-campaign-notice">
                This campaign is active and sending emails on schedule. {existingCampaign.sequence.filter(s => s.sent).length}/{existingCampaign.sequence.length} emails sent.
              </div>
            )}

            {/* Template Library */}
            <div>
              <h4 className="font-medium text-slate-900 mb-3">
                {isActive ? 'Campaign Sequence' : 'Select Email Templates'}
              </h4>
              <div className="space-y-2" data-testid="template-list">
                {templates.map(tmpl => {
                  const isSelected = selected.some(s => s.key === tmpl.key);
                  const selItem = selected.find(s => s.key === tmpl.key);
                  const isPreviewOpen = previewKey === tmpl.key;
                  const sentInCampaign = existingCampaign?.sequence?.find(s => s.template_key === tmpl.key && s.sent);

                  return (
                    <div key={tmpl.key} className={`border rounded-lg transition-all ${isSelected ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
                      <div className="flex items-center gap-3 p-3">
                        {/* Checkbox */}
                        {!isActive && (
                          <button
                            onClick={() => toggleTemplate(tmpl.key)}
                            className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 hover:border-blue-400'
                            }`}
                            data-testid={`template-toggle-${tmpl.key}`}
                          >
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </button>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{tmpl.label}</span>
                            <Badge className={`text-[10px] ${CATEGORY_COLORS[tmpl.category] || 'bg-slate-100'}`}>
                              {tmpl.category}
                            </Badge>
                            {sentInCampaign && <Badge className="bg-emerald-500 text-[10px]">Sent</Badge>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{tmpl.description}</p>
                          <p className="text-xs text-slate-400 mt-0.5">Subject: {tmpl.subject}</p>
                        </div>

                        {/* Day selector */}
                        {isSelected && !isActive && (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <Calendar className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-xs text-slate-500">Day</span>
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={selItem?.day || tmpl.default_day}
                              onChange={(e) => updateDay(tmpl.key, e.target.value)}
                              className="w-14 px-2 py-1 text-xs border rounded text-center"
                              data-testid={`day-input-${tmpl.key}`}
                            />
                          </div>
                        )}

                        {isActive && selItem && (
                          <span className="text-xs text-slate-500 flex-shrink-0">Day {selItem.day}</span>
                        )}

                        {/* Preview button */}
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handlePreview(tmpl.key)}
                          className="flex-shrink-0 h-8 px-2"
                          data-testid={`preview-btn-${tmpl.key}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {isPreviewOpen ? 'Hide' : 'Preview'}
                        </Button>
                      </div>

                      {/* Preview panel */}
                      {isPreviewOpen && previewHtml && (
                        <div className="border-t bg-white p-4" data-testid={`preview-panel-${tmpl.key}`}>
                          <div className="bg-slate-50 rounded p-3 mb-2">
                            <p className="text-xs text-slate-500 mb-1">Subject: <strong>{previewHtml.subject}</strong></p>
                          </div>
                          <div
                            className="prose prose-sm max-w-none text-sm [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5"
                            dangerouslySetInnerHTML={{ __html: previewHtml.body }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected Summary */}
            {selected.length > 0 && !isActive && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-2">Campaign Sequence ({selected.length} emails)</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {sortedSelected.map(s => {
                    const tmpl = templates.find(t => t.key === s.key);
                    return (
                      <Badge key={s.key} variant="outline" className="text-xs">
                        Day {s.day}: {tmpl?.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            {!isActive && (
              <div className="flex gap-2 border-t pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving || selected.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-campaign-btn"
                >
                  {saving ? 'Saving...' : isDraft ? 'Update Draft' : 'Save Campaign Draft'}
                </Button>
                {isDraft && (
                  <Button
                    onClick={handleActivate}
                    disabled={activating}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="activate-campaign-btn"
                  >
                    <Play className="h-4 w-4 mr-1" />
                    {activating ? 'Activating...' : 'Approve & Activate'}
                  </Button>
                )}
                <Button variant="outline" onClick={onClose}>Cancel</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
